import { productsPage } from "@/mocks/products";
import GlobalBottomCta from "@/components/feature/GlobalBottomCta";
import PageMeta from "@/components/feature/PageMeta";
import ScrollReveal from "@/components/feature/ScrollReveal";

const pageMeta = {
  title: "產品介紹｜來加綉綉紅豆餅",
  description: "來加綉綉紅豆餅提供經典紅豆、奶油卡士達、芋泥香香、花生芝麻、起司鹹甜與季節限定等豐富口味。每一顆都是現烤出爐，外酥內香。",
  keywords: "紅豆餅口味,車輪餅口味,經典紅豆餅,奶油卡士達,芋泥紅豆餅,現烤紅豆餅,手作車輪餅",
  ogType: "product",
};

const productImages = [
  "https://readdy.ai/api/search-image?query=golden%20brown%20taiyaki%20red%20bean%20cake%20classic%20sweet%20red%20bean%20filling%20visible%20close-up%20food%20photography%20warm%20amber%20lighting%20Japanese%20street%20food%20crispy%20golden%20shell%20cream%20background%20appetizing&width=600&height=400&seq=prod-p-1&orientation=landscape",
  "https://readdy.ai/api/search-image?query=taiyaki%20with%20creamy%20custard%20butter%20filling%20oozing%20out%20golden%20brown%20close-up%20food%20photography%20warm%20soft%20lighting%20Japanese%20street%20snack%20rich%20cream%20filling%20appetizing&width=600&height=400&seq=prod-p-2&orientation=landscape",
  "https://readdy.ai/api/search-image?query=taiyaki%20with%20purple%20taro%20filling%20cross%20section%20showing%20thick%20purple%20paste%20close-up%20food%20photography%20warm%20lighting%20Taiwanese%20artisan%20street%20snack%20appetizing&width=600&height=400&seq=prod-p-3&orientation=landscape",
  "https://readdy.ai/api/search-image?query=taiyaki%20with%20peanut%20and%20sesame%20filling%20cross%20section%20golden%20brown%20close-up%20food%20photography%20warm%20lighting%20nutty%20aroma%20artisan%20snack%20appetizing&width=600&height=400&seq=prod-p-4&orientation=landscape",
  "https://readdy.ai/api/search-image?query=savory%20taiyaki%20with%20cheese%20filling%20golden%20brown%20crispy%20shell%20close-up%20food%20photography%20warm%20lighting%20salty%20sweet%20combination%20creative%20snack%20appetizing&width=600&height=400&seq=prod-p-5&orientation=landscape",
  "https://readdy.ai/api/search-image?query=seasonal%20limited%20edition%20taiyaki%20with%20colorful%20fruit%20filling%20creative%20Japanese%20street%20food%20warm%20lighting%20close-up%20food%20photography%20appetizing%20special%20edition&width=600&height=400&seq=prod-p-6&orientation=landscape",
];

const featureIcons = [
  { icon: "ri-fire-line", label: "餅皮香氣" },
  { icon: "ri-scales-3-line", label: "內餡比例" },
  { icon: "ri-palette-line", label: "口味設計" },
  { icon: "ri-temp-hot-line", label: "冷熱皆宜" },
];

const enjoyIcons = [
  { icon: "ri-fire-line", label: "現場現烤" },
  { icon: "ri-truck-line", label: "冷凍宅配" },
  { icon: "ri-gift-2-line", label: "禮盒分享" },
];

export default function Products() {
  const { hero, features, products, howToEnjoy } = productsPage;

  return (
    <main className="pt-16 md:pt-20">
      <PageMeta {...pageMeta} />
      {/* Hero */}
      <section className="relative min-h-[380px] md:min-h-[460px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-redbean-primary/40">
          <img
            src="https://readdy.ai/api/search-image?query=variety%20of%20taiyaki%20red%20bean%20cakes%20different%20flavors%20displayed%20on%20wooden%20board%20red%20bean%20taro%20cream%20sweet%20potato%20colorful%20assortment%20warm%20bakery%20atmosphere%20artisan%20handmade%20Taiwanese%20street%20snack%20food%20photography%20warm%20golden%20lighting%20appetizing&width=1600&height=900&seq=prod-hero-1&orientation=landscape"
            alt="來加綉綉多種口味紅豆餅展示"
            className="w-full h-full object-cover object-center"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-redbean-coffee/55 via-redbean-coffee/45 to-redbean-coffee/60" />
        </div>
        <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-8 text-center" style={{ maxWidth: "900px" }}>
          <span className="inline-block px-4 py-1.5 bg-redbean-gold text-redbean-coffee text-sm font-semibold rounded-full mb-6">
            {hero.tag}
          </span>
          <h1
            className="font-heading font-black text-redbean-cream mb-5 max-w-3xl mx-auto text-balance"
            style={{ fontSize: "clamp(2rem, 5vw, 2.75rem)", lineHeight: 1.3 }}
          >
            {hero.title}
          </h1>
          <p className="text-redbean-cream/85 max-w-xl mx-auto" style={{ fontSize: "1.1875rem", lineHeight: 1.75 }}>
            {hero.subtitle}
          </p>
        </div>
      </section>

      {/* Features */}
      <ScrollReveal delay={100}>
      <section className="py-16 md:py-20 bg-redbean-cream">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: "1200px" }}>
          <h2
            className="font-heading font-bold text-redbean-coffee text-center mb-10 md:mb-12"
            style={{ fontSize: "clamp(1.5rem, 3vw, 2.125rem)", lineHeight: 1.4 }}
          >
            {features.sectionTitle}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {features.items.map((item, idx) => (
              <ScrollReveal key={idx} delay={idx * 100}>
                <div className="bg-white rounded-card p-6 md:p-7 shadow-soft text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-redbean-primary/10 text-redbean-primary">
                    <span className="w-7 h-7 flex items-center justify-center">
                      <i className={`${featureIcons[idx].icon} text-2xl`} />
                    </span>
                  </div>
                  <h3
                    className="font-heading font-bold text-redbean-coffee mb-2"
                    style={{ fontSize: "1.125rem", lineHeight: 1.45 }}
                  >
                    {item.title}
                  </h3>
                  <p className="text-redbean-coffee/60 text-[1rem] leading-relaxed">{item.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* Products Grid */}
      <ScrollReveal delay={150}>
      <section className="py-16 md:py-20 bg-redbean-lightcream">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: "1200px" }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {products.map((product, idx) => (
              <ScrollReveal key={product.id} delay={idx * 100}>
                <div
                  className="bg-white rounded-card overflow-hidden shadow-soft hover:shadow-card transition-shadow duration-300 group"
                >
                  <div className="w-full" style={{ height: "220px" }}>
                    <img
                      src={productImages[product.id - 1]}
                      alt={product.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="p-5 md:p-6">
                    <h3
                      className="font-heading font-bold text-redbean-coffee mb-2"
                      style={{ fontSize: "1.25rem", lineHeight: 1.5 }}
                    >
                      {product.name}
                    </h3>
                    <p className="text-redbean-coffee/60 text-[1rem] leading-relaxed mb-3">
                      {product.shortDesc}
                    </p>
                    <p className="text-redbean-caramel text-sm mb-4">
                      <span className="font-semibold">適合：</span>
                      {product.audience}
                    </p>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-redbean-caramel text-sm font-medium hover:text-redbean-primary transition-colors"
                    >
                      了解更多
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line" />
                      </span>
                    </button>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* How to Enjoy */}
      <ScrollReveal delay={200}>
      <section className="py-16 md:py-20 bg-redbean-cream">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: "1200px" }}>
          <h2
            className="font-heading font-bold text-redbean-coffee text-center mb-10 md:mb-12"
            style={{ fontSize: "clamp(1.5rem, 3vw, 2.125rem)", lineHeight: 1.4 }}
          >
            {howToEnjoy.sectionTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {howToEnjoy.items.map((item, idx) => (
              <ScrollReveal key={idx} delay={idx * 120}>
                <div className="bg-white rounded-card p-6 md:p-7 shadow-soft text-center">
                  <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center rounded-full bg-redbean-primary/10 text-redbean-primary">
                    <span className="w-7 h-7 flex items-center justify-center">
                      <i className={`${enjoyIcons[idx].icon} text-2xl`} />
                    </span>
                  </div>
                  <h3
                    className="font-heading font-bold text-redbean-coffee mb-2"
                    style={{ fontSize: "1.25rem", lineHeight: 1.5 }}
                  >
                    {item.title}
                  </h3>
                  <p className="text-redbean-coffee/60 text-[1rem] leading-relaxed">{item.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* Bottom CTA */}
      <GlobalBottomCta />
    </main>
  );
}